import { getMyself } from '@/api/profile'
import { ref, computed } from 'vue'
import { getStorage, setStorage } from '@/utils/helpers'
import { VRIO_KEY } from '@/constants'
import { postAuthLogin, postAuthLogout } from '@/api/auth'
import { NOTIFICATION_KEY } from '@/constants'

const isVrio = ref(getStorage(VRIO_KEY) ? true : false)
const me = ref(null)
const authStatus = ref('unknown')
let authCheckPromise = null

export default function useAuth() {
  const auth = async (payload) => {
    try {
      await postAuthLogin(payload)

      // При refresh не требуем повторную загрузку профиля.
      if (payload.grant_type === 'refresh_token') {
        authStatus.value = me.value ? 'authenticated' : 'unknown'
        return
      }

      const profile = await getMe(true)
      if (!profile) {
        throw new Error('Не удалось получить профиль после авторизации')
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const getMe = async (force = false) => {
    if (me.value && !force) {
      authStatus.value = 'authenticated'
      return me.value
    }

    try {
      const resp = await getMyself()
      me.value = resp
      authStatus.value = 'authenticated'
      return me.value
    } catch (error) {
      if (error.response?.status === 401) {
        me.value = null
        authStatus.value = 'unauthenticated'
        return null
      }
      console.log(error)
      throw error
    }
  }

  const ensureAuthenticated = async () => {
    if (authStatus.value === 'authenticated') return true
    if (authStatus.value === 'unauthenticated') return false

    if (!authCheckPromise) {
      authCheckPromise = getMe()
        .then((profile) => Boolean(profile))
        .finally(() => {
          authCheckPromise = null
        })
    }

    return await authCheckPromise
  }

  /**
   * Проверяет валидность пароля
   * @returns {boolean}
   */
  const isTemporaryPassword = async () => {
    const profile = me.value ?? (await getMe())
    const isValid = ref(true) // возвращаем false только при явном признаки не валидности пароля

    if (profile?.must_change_password) {
      isValid.value = false
    }

    return isValid
  }

  /**
   * Проверяет, что пароль истек(пароль нужно обновить за 5 дней)
   * @returns {boolean}
   */
  const isCriticalExpiredPassword = computed(() => {
    if (me.value?.days_before_password_expires <= 5) return true
    return false
  })
  /**
   * Проверяет, что пароль скоро истекает
   * @returns {boolean}
   */
  const isExpireSoonPassword = computed(() => {
    if (me.value?.days_before_password_expires <= 21) return true
    return false
  })

  const removeTokens = () => {
    me.value = null
    authStatus.value = 'unauthenticated'
  }

  const setVrio = (state) => {
    setStorage(VRIO_KEY, state)
    isVrio.value = state
  }

  const logout = async () => {
    try {
      await postAuthLogout()
    } catch (error) {
      console.error(error)
    }
    removeTokens()
    setVrio(false)
    localStorage.removeItem(NOTIFICATION_KEY) // удаляем информацию об показанных уведомлениях
    window.location.replace('/auth')
  }

  return {
    me,
    authStatus,
    isAuthenticated: computed(() => authStatus.value === 'authenticated'),
    isAuthResolved: computed(() => authStatus.value !== 'unknown'),
    auth,
    getMe,
    ensureAuthenticated,
    isTemporaryPassword,
    isCriticalExpiredPassword,
    isExpireSoonPassword,
    removeTokens,
    isVrio,
    setVrio,
    logout,
  }
}
